import i18next from "i18next"
import { containsUnsafeTextContent } from "utils/unsafeTextContent"
import {
  validateEmailAddress,
  getEmailValidationErrorMessage,
} from "utils/emailValidation"
import { getPhoneLengthError, trimPhoneToMaxLength } from "utils/phoneUtils"
import { isValidPhoneNumber } from "libphonenumber-js"
import {
  getSyncInvalidImageUrlMessage,
  validateExactImageUrlDimensions,
} from "utils/imageUrlValidation"
import {
  PERSON_NAME_MAX_LENGTH,
  validatePersonName,
} from "utils/personNameValidation"

export const RIDER_NAME_MAX_LENGTH = PERSON_NAME_MAX_LENGTH
export const RIDER_PHONE_MAX_LENGTH = 20
export const RIDER_PROFILE_URL_MAX_LENGTH = 2048
export const RIDER_STATUS_REASON_MAX_LENGTH = 500
export const REASON_REQUIRED_STATUSES = ["Suspended", "Rejected"]

const t = (key, options) => i18next.t(key, options)

export const validateRiderName = name => {
  const trimmed = String(name || "").trim()
  if (trimmed && containsUnsafeTextContent(trimmed)) {
    return t("This field cannot contain HTML or script content.")
  }
  return validatePersonName(name, { required: true })
}

export const validateRiderPhone = (phone, { required = true } = {}) => {
  const trimmed = trimPhoneToMaxLength(String(phone || "").trim())
  if (!trimmed) return required ? t("Field is required") : ""

  const lengthError = getPhoneLengthError(trimmed)
  if (lengthError === "TOO_LONG" || lengthError === "INVALID_LENGTH") {
    return t("Please enter a valid phone number")
  }

  if (!isValidPhoneNumber(trimmed)) {
    return t("Please enter a valid phone number")
  }

  return ""
}

export const validateRiderEmail = email => {
  const trimmed = String(email || "").trim()
  if (!trimmed) return ""
  const result = validateEmailAddress(trimmed)
  if (!result.valid) {
    return getEmailValidationErrorMessage(result.code)
  }
  return ""
}

export const validateRiderBranch = branchId => {
  if (!branchId) return t("Field is required")
  return ""
}

export const validateRiderProfilePhotoUrl = url => {
  const trimmed = String(url || "").trim()
  if (!trimmed) return ""
  if (trimmed.length > RIDER_PROFILE_URL_MAX_LENGTH) {
    return t("Profile photo URL cannot exceed {{max}} characters", {
      max: RIDER_PROFILE_URL_MAX_LENGTH,
    })
  }
  if (containsUnsafeTextContent(trimmed)) {
    return t("This field cannot contain HTML or script content.")
  }
  return getSyncInvalidImageUrlMessage(trimmed)
}

export const validateRiderProfilePhotoUrlDimensions = async (
  url,
  { width, height } = {}
) => {
  const syncError = validateRiderProfilePhotoUrl(url)
  if (syncError) {
    return syncError
  }

  const trimmed = String(url || "").trim()
  if (!trimmed) {
    return ""
  }

  const result = await validateExactImageUrlDimensions(trimmed, {
    width,
    height,
  })

  if (result.valid) {
    return ""
  }

  if (result.code === "wrong_dimensions") {
    return t("Image dimensions must be {{itemWidth}}x{{itemHeight}} pixels.", {
      itemWidth: width || result.expectedWidth,
      itemHeight: height || result.expectedHeight,
    })
  }

  return t(
    "Please enter a valid image URL (JPEG, PNG, or WebP). The link must open an image, not a webpage or document."
  )
}

export const validateRiderStatusReason = (status, reason) => {
  if (!REASON_REQUIRED_STATUSES.includes(status)) return ""
  const trimmed = String(reason || "").trim()
  if (!trimmed) return t("Field is required")
  if (trimmed.length > RIDER_STATUS_REASON_MAX_LENGTH) {
    return t("Reason cannot exceed {{max}} characters", {
      max: RIDER_STATUS_REASON_MAX_LENGTH,
    })
  }
  if (containsUnsafeTextContent(trimmed)) {
    return t("This field cannot contain HTML or script content.")
  }
  return ""
}

export const validateRiderForm = (form, { isEditMode } = {}) => {
  const errors = {
    name: validateRiderName(form.name),
    phone: isEditMode ? "" : validateRiderPhone(form.phone, { required: true }),
    email: validateRiderEmail(form.email),
    branchId: validateRiderBranch(form.branchId),
    reason: validateRiderStatusReason(form.status, form.reason),
    profilePhoto:
      form.profilePhotoType === "URL"
        ? validateRiderProfilePhotoUrl(form.profilePhoto)
        : "",
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => Boolean(message))
  )
}

export const hasRiderFormErrors = errors => Object.keys(errors).length > 0
